"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useFavorites() {
  const queryClient = useQueryClient();

  const { data: favorites = [], isLoading } = useQuery<string[]>({
    queryKey: ["favorites"],
    queryFn: async () => {
      const response = await fetch("/api/favorites");
      if (!response.ok) throw new Error("Failed to load favorites");
      const data = await response.json();
      return data.favorites;
    },
    staleTime: 30 * 1000,
  });

  const mutation = useMutation({
    mutationFn: async ({ essenceId, favorite }: { essenceId: string; favorite: boolean }) => {
      const response = await fetch("/api/favorites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ essenceId, favorite }),
      });
      if (!response.ok) throw new Error("Failed to update favorite");
      return response.json();
    },
    onMutate: async ({ essenceId, favorite }) => {
      await queryClient.cancelQueries({ queryKey: ["favorites"] });
      const previous = queryClient.getQueryData<string[]>(["favorites"]) ?? [];
      const next = favorite
        ? [...previous, essenceId]
        : previous.filter((id) => id !== essenceId);
      queryClient.setQueryData(["favorites"], next);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["favorites"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
    },
  });

  return {
    favorites,
    isLoading,
    isPending: mutation.isPending,
    toggle: mutation.mutateAsync,
  };
}
